import React, { useCallback, useContext, useMemo, useState } from 'react';
import { GoodsCard2Column } from '@banhala/ui-kit';
import { useAtomValue } from 'jotai';
import { isAndroid, isNotNil, useNavigator } from 'shared/src/utils';
import { RankingGoodsListHeader } from './components/RankingGoodsListHeader';
import { useRankingGoods } from './queries/useRankingGoods';
import { rankingFilterAtom } from './states/rankingFilter.atom';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  convertRankingGoodsItemToGoodsData,
  type RankingGoodsCardItem,
} from './utils/convertRankingGoodsItemToGoodsData';
import { useAddLikeGoods } from 'shared/src/hooks';
import { useSyncLocalGoodsLike } from 'shared/src/hooks/useSyncLocalGoodsLike';
import { useRemoveLikeGoods } from 'shared/src/queries';
import { bridge } from 'shared/src/utils';

export const GoodsRanking = () => {
  const filter = useAtomValue(rankingFilterAtom);
  const { navigator } = useNavigator();

  const selectedCategorySno =
    filter.selectedSubCategory?.query_value.category_sno ??
    filter.selectedCategory?.query_value.category_sno ??
    null;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, fetchNextPage, hasNextPage, refetch } = useRankingGoods({
    filter: 'best',
    market_type_sno:
      filter.selectedMarketType?.query_value.market_type_sno ?? undefined,
    category_sno: selectedCategorySno ?? undefined,
    'age_tags[]': filter.selectedAge?.query_value['age_tags[]'] ?? undefined,
    period: filter.selectedPeriod?.value ?? undefined,
  });

  const list = useMemo(() => {
    if (!data?.goods) return [];
    return data.goods.map(convertRankingGoodsItemToGoodsData);
  }, [data?.goods]);

  const [likeMap, setLikeMap] = useState<{ [key: number]: boolean }>({});

  useMemo(() => {
    if (!data?.goods) return;
    const initialLikeMap: { [key: number]: boolean } = {};
    for (const goods of data.goods) {
      initialLikeMap[goods.sno] = isNotNil(goods.like);
    }
    setLikeMap(prev => ({ ...initialLikeMap, ...prev }));
  }, [data?.goods]);

  const { addLikeGoods } = useAddLikeGoods({
    listParams: undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
    screen: 'RANKING',
  });

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();

  useSyncLocalGoodsLike(setLikeMap);

  const homeScroll = useContext(HomeScrollContext);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      homeScroll?.onScrollY(e.nativeEvent.contentOffset.y);
    },
    [homeScroll],
  );

  const handlePressItem = (pressed: RankingGoodsCardItem) => {
    const { item } = pressed;
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering
        ? {
            discount_rate: item.first_page_rendering.discount_rate,
            goods_name: item.first_page_rendering.goods_name,
            original_price: item.first_page_rendering.original_price,
            price: item.first_page_rendering.price,
            cover_image: item.first_page_rendering.cover_image,
            market_image: item.first_page_rendering.market_image ?? undefined,
            market_name: item.first_page_rendering.market_name ?? undefined,
          }
        : undefined,
    });
  };

  const handlePressLike = async (pressed: RankingGoodsCardItem) => {
    const { item } = pressed;

    if (!likeMap[item.sno]) {
      addLikeGoods(item.sno, { SCREEN: 'RANKING' });
      return;
    }

    await mutateRemoveLikeGoods({ sno: item.sno });
    bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
    setLikeMap(prev => ({ ...prev, [item.sno]: false }));
  };

  return (
    <GoodsCard2Column<RankingGoodsCardItem>
      list={list}
      likeMap={likeMap}
      showRanking={list.some(item => isNotNil(item.display.ranking))}
      keyExtractor={(item, index) => (item.sno ?? index).toString()}
      onPressItem={handlePressItem}
      onPressLike={handlePressLike}
      rankingBackgroundColor={'black'}
      windowSize={isAndroid ? 2 : 10}
      initialNumToRender={4}
      refreshing={isRefreshing}
      onRefresh={() => {
        setIsRefreshing(true);
        refetch().finally(() => setIsRefreshing(false));
      }}
      ListHeaderComponent={
        <React.Suspense fallback={<></>}>
          <RankingGoodsListHeader
            lastUpdatedBeforeMessage={data?.last_updated_before_message}
          />
        </React.Suspense>
      }
      onEndReached={() => {
        if (hasNextPage) {
          fetchNextPage();
        }
      }}
      showSkeleton={true}
      onEndReachedThreshold={1}
      {...({ onScroll: handleScroll } as Record<string, unknown>)}
    />
  );
};
